import type { Uppy, UppyFile } from '@uppy/core';
import BasePlugin from '@uppy/core/lib/BasePlugin';

type PluginOptions = {
  id?: string;
  endpoint: string;   // e.g., /uploads/batch_check
  albumId: number;
  onDuplicateDecision?: (duplicates: string[]) => Promise<boolean>;
};

type Meta = Record<string, any>;
type Body = Record<string, never>;

class DuplicateDetectionPlugin extends BasePlugin<PluginOptions, Meta, Body> {
  private filesAddedCallback: (files: UppyFile<Meta, Body>[]) => Promise<void>;

  constructor(uppy: Uppy<Meta, Body>, opts: PluginOptions) {
    super(uppy, opts);

    this.id = opts.id || 'DuplicateDetectionPlugin';
    this.type = 'modifier'; // non-UI plugin

    // Store the callback as a class property
    this.filesAddedCallback = this.handleFilesAdded.bind(this);
  }

  async checkDuplicates(files: UppyFile<Meta, Body>[]): Promise<string[]> {
    const { endpoint, albumId } = this.opts;

    const filenames = files.map(file => file.name).filter(Boolean) as string[];

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          album_id: albumId,
          filenames: filenames,
        }),
      });

      if (!response.ok) throw new Error(`API error: ${response.statusText}`);

      const data = await response.json();
      return data.duplicates || [];
    } catch (error) {
      console.error('Duplicate check failed:', error);
      this.uppy.info(`Duplicate check failed: ${error.message}`, 'error', 5000);
      return [];
    }
  }

  private async handleFilesAdded(files: UppyFile<Meta, Body>[]) {
    const duplicates = await this.checkDuplicates(files);

    if (duplicates.length === 0) return;

    if (duplicates.length > 0) {
      let shouldSkip = true;
      if (this.opts.onDuplicateDecision) {
        shouldSkip = await this.opts.onDuplicateDecision(duplicates);
      }

      if (shouldSkip) {
        files.forEach(file => {
          if (file.name && duplicates.includes(file.name)) {
            this.uppy.info(`Already uploaded: ${file.name}`, 'warning', 3000);
            this.uppy.removeFile(file.id);
          }
        });
      }
    }
  }

  install() {
    this.uppy.on('files-added', this.filesAddedCallback);
  }

  uninstall() {
    this.uppy.off('files-added', this.filesAddedCallback);
  }
}

export default DuplicateDetectionPlugin;